<template>
  <VCard
    dark
    :height="showDetails ? 520 : 150"
  >
    <VToolbar dark>
      <VToolbarTitle> {{ ruleName }} </VToolbarTitle>
      <VSpacer />
      <VMenu
        origin="center center"
        transition="scale-transition"
        class="menu"
      >
        <VBtn
          slot="activator"
          dark
          icon
        >
          <VIcon>more_vert</VIcon>
        </VBtn>
        <VList>
          <VListTile
            v-for="(menuItem, i) in menuItems"
            :key="i"
            @click.stop="onRuleMenuItemClicked(i, currentRule)"
          >
            <VListTileTitle color="red">
              {{ menuItem.title }}
            </VListTileTitle>
          </VListTile>
        </VList>
      </VMenu>
    </VToolbar>
    <VCardText>
      <VContainer
        ma-0
        pa-0
      >
        <VLayout
          row
          wrap
        >
          <VFlex xs12>
            <VCheckbox
              v-model="ruleEnabled"
              label="Enabled"
            />
          </VFlex>
          <VFlex
            v-if="showDetails"
            xs12
          >
            <VSelect
              v-model="ruleType"
              :disabled="!ruleEnabled"
              :items="Object.values(ruleTypeDict)"
              menu-props="auto"
              label="Type"
              hide-details
              outline
            />
          </VFlex>
          <div
            v-if="ruleType === 'Stay in contact' && showDetails"
            class="options"
          >
            <VFlex xs12>
              <VCheckbox
                v-model="repeated"
                :label="`Repeated`"
                :disabled="!ruleEnabled"
              />
            </VFlex>
            <VFlex xs12>
              <VCheckbox
                v-if="applyToNewOption"
                :label="`Apply to new contacts`"
                :disabled="!ruleEnabled"
              />
            </VFlex>
            <VFlex xs12>
              <VTextField
                v-model="frequency"
                :disabled="!ruleEnabled"
                :mask="mask"
                :rules="rules.nonZero"
                label="How often (days)"
                outline
                @click:append-outer="onAddDayClicked('frequency')"
                @click:prepend="onRemoveDayClicked('frequency')"
              />
            </VFlex>
            <VFlex xs12>
              <VTextField
                v-model="skipContactedWithin"
                :disabled="!ruleEnabled"
                :mask="mask"
                label="Skip if contacted within days"
                outline
              />
            </VFlex>
          </div>
          <div
            v-if="ruleType === 'Event followup' && showDetails"
            class="options"
          >
            <VFlex xs12>
              <VTextField
                v-model="remindDays"
                class="numberSelect"
                type="number"
                :disabled="!ruleEnabled"
                :rules="rules.numberRules"
                append-outer-icon="add"
                prepend-icon="remove"
                :label="`Remind ${remindDays >= 0 ? `after` : `before` } days`"
                outline
                @click:append-outer="onAddDayClicked('remindDays')"
                @click:prepend="onRemoveDayClicked('remindDays')"
              />
            </VFlex>
            <VFlex xs12>
              <VCheckbox
                v-model="applyToBirthday"
                :label="`Apply to birthday`"
                :disabled="!ruleEnabled"
              />
            </VFlex>
            <VFlex
              v-if="!applyToBirthday"
              xs12
            >
              <VueCtkDateTimePicker
                v-model="scheduledDateTime"
                class="datePicker"
                label="Select Date"
                :disabled="!ruleEnabled"
                :class="[{dateDisabled: !ruleEnabled}]"
                format="YYYY-MM-DD"
                dark
                formatted="ll"
                only-date
                input-size="lg"
              />
            </VFlex>
            <VFlex xs12>
              <VTextField
                v-model="skipContactedWithin"
                :disabled="!ruleEnabled"
                :mask="mask"
                label="Skip if contacted within days"
                outline
              />
            </VFlex>
          </div>
        </VLayout>
      </VContainer>
    </VCardText>
  </VCard>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { automationMapActions } from '@/store/utils'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import { CREATE_PROFILE_RULE, UPDATE_PROFILE_RULE } from '@/backend/graphql/automationGQL'
import _ from 'lodash'
import moment from 'moment-timezone'
export default {
  name: 'RuleDialogCard',
  components: {
    VueCtkDateTimePicker
  },
  props: {
    ruleIndex: {
      type: Number,
      default: null
    },
    isRuleEnabled: {
      type: Function,
      default: function (index) {}
    },
    setRuleEnabled: {
      type: Function,
      default: function (index) {}
    },
    showDetails: {
      type: Boolean,
      default: true
    },
    applyToNewOption: {
      type: Boolean,
      default: false
    },
    defaultRuleData: {
      type: Object,
      default: function () {
        return {}
      }
    },
    detailDialog: {
      type: Boolean,
      default: false
    },
    detailDialogTitle: {
      type: String,
      default: ''
    },
    updateRuleGql: {
      type: Object,
      default: function () {
        return UPDATE_PROFILE_RULE
      }
    },
    createRuleGql: {
      type: Object,
      default: function () {
        return CREATE_PROFILE_RULE
      }
    },
    ruleData: {
      type: Object,
      default: function () {
        return {}
      }
    },
    saveMethod: {
      type: Function,
      default: function () {
        return {}
      }
    },
    saveMethodGql: {
      type: Object,
      default: function () {
        return {}
      }
    },
    editRuleIndex: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      mask: '###',
      ruleTypeDict: {
        STAY_IN_CONTACT: 'Stay in contact',
        EVENT_FOLLOWUP: 'Event followup'
      },
      maxNumber: 999,
      minNumber: -999,
      rules: {
        numberRules: [
          v => (v === 0 || !!v) || 'Required',
          (v) => v <= this.maxNumber || `Should be less than ${this.maxNumber} days`,
          v => v >= this.minNumber || `Should be greater than ${this.minNumber} days`,
          v => /^-?\d+$/.test(v) || 'Email must be number'
        ],
        nonZero: [
          value => !!value || 'Required',
          value => value > 0 || 'Should be greater than one day'
        ]
      },
      menuItems: [
        { title: 'Edit' },
        { title: 'Delete' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      allRules: 'automation/getAllRules'
    }),
    currentRule () {
      return this.allRules[this.ruleIndex]
    },
    ruleName: {
      get () {
        return this.allRules[this.ruleIndex].name
      }
    },
    ruleEnabled: {
      get () {
        return this.isRuleEnabled(this.ruleIndex)
      },
      set () {
        this.setRuleEnabled(this.ruleIndex)
      }
    },
    ruleType: {
      get () {
        return this.ruleTypeDict[this.allRules[this.ruleIndex].ruleType]
      },
      set (value) {
        const entries = Object.entries(this.ruleTypeDict)
        entries.some((item) => {
          if (item[1] === value) {
            this.updateRuleTypeByIndex({ index: this.ruleIndex, value: item[0] })
            return true
          }
          return false
        })
      }
    },
    repeated: {
      get () {
        return this.allRules[this.ruleIndex].repeated
      },
      set (value) {
        this.updateRepeatedByIndex({ index: this.ruleIndex, value })
      }
    },
    applyToNew: {
      get () {
        return this.allRules[this.ruleIndex].applyToNew
      },
      set (value) {
        this.updateApplyToNewByIndex({ index: this.ruleIndex, value })
      }
    },
    frequency: {
      get () {
        return this.allRules[this.ruleIndex].frequency
      },
      set (value) {
        this.updateFrequencyByIndex({ index: this.ruleIndex, value })
      }
    },
    skipContactedWithin: {
      get () {
        return this.allRules[this.ruleIndex].skipContactedWithin
      },
      set (value) {
        this.updateSkipContactedWithinByIndex({ index: this.ruleIndex, value })
      }
    },
    remindDays: {
      get () {
        return this.allRules[this.ruleIndex].remindDays
      },
      set (value) {
        this.updateRemindDaysByIndex({ index: this.ruleIndex, value })
      }
    },
    applyToBirthday: {
      get () {
        return this.allRules[this.ruleIndex].applyToBirthday
      },
      set (value) {
        this.updateApplyToBirthdayByIndex({ index: this.ruleIndex, value })
      }
    },
    scheduledDateTime: {
      get () {
        return this.allRules[this.ruleIndex].scheduledDateTime
      },
      set (val) {
        const value = moment.tz(val, 'YYYY-MM-DD', this.allRules[this.ruleIndex].timeZone).toISOString()
        this.updateScheduledDateTimeByIndex({ index: this.ruleIndex, value })
      }
    },
    _saveMethod: {
      get () {
        return this.saveMethod
      },
      set (val) {
        this.$emit('update:saveMethod', val)
      }
    },
    _saveMethodGql: {
      get () {
        return this.saveMethodGql
      },
      set (val) {
        this.$emit('update:saveMethodGql', val)
      }
    },
    _editRuleIndex: {
      get () {
        return this.editRuleIndex
      },
      set (val) {
        this.$emit('update:editRuleIndex', val)
      }
    },
    _ruleData: {
      get () {
        return this.ruleData
      },
      set (val) {
        this.$emit('update:ruleData', val)
      }
    },
    _detailDialog: {
      get () {
        return this.detailDialog
      },
      set (val) {
        this.$emit('update:detailDialog', val)
      }
    },
    _detailDialogTitle: {
      get () {
        return this.detailDialogTitle
      },
      set (val) {
        this.$emit('update:detailDialogTitle', val)
      }
    }
  },
  methods: {
    ...mapActions(automationMapActions),
    async onRuleMenuItemClicked (index, data) {
      switch (index) {
        case 0:
          this.onDetailDialogOpen(this.menuItems[index].title, data)
          break
        case 1:
          await this.deleteRule({ ruleData: { id: data.id } })
          break
        default:
          break
      }
    },
    onAddDayClicked (name) {
      const val = _.get(this.currentRule, name, null)
      if (val !== null) {
        switch (name) {
          case 'frequency':
            this.frequency = val + 1
            break
          case 'remindDays':
            this.remindDays = val + 1
            break
          default:
            break
        }
      }
    },
    onRemoveDayClicked (name) {
      const val = _.get(this.currentRule, name, null)
      if (val !== null) {
        switch (name) {
          case 'frequency':
            this.frequency = val - 1
            break
          case 'remindDays':
            this.remindDays = val - 1
            break
          default:
            break
        }
      }
    },
    onDetailDialogOpen (titleName, data) {
      this._detailDialogTitle = titleName
      this._ruleData = data || this.defaultRuleData
      this._editRuleIndex = null
      this._saveMethod = this.updateRule
      this._saveMethodGql = this.updateRuleGql
      this.allRules.some((rule, index) => {
        if (rule.id === data.id) {
          this._editRuleIndex = index
          return true
        }
        return false
      })
      this._detailDialog = true
    }
  }

}
</script>

<style scoped>
  .options {
    width: 100%;
    margin: 15px 0px 15px 0px;
  }
  .numberSelect {
    height: 60px;
  }
  .datePicker {
    margin-bottom: 20px;
  }
  .datePicker >>> .field.is-dark .field-input {
    color: white;
    border: 2px solid hsla(0,0%,100%,.7);
  }
  .dateDisabled >>> .field.is-disabled .field-input {
    color: gray;
    background: bottom;
  }
  .dateDisabled >>> .field.is-dark.is-disabled .field-label {
    color: hsla(0,0%,100%,.7);
  }
  .dateDisabled >>> .field.is-disabled .field-input::placeholder{
    color: hsla(0,0%,100%,.7)
  }
  /* hot fix for now ....*/
  .menu { z-index: 202 }
</style>
